import React, { useMemo } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const DecisionLogWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const currentPhaseIndex = state?.currentPhaseIndex ?? 0;

  // Derive "decisions" from accepted contributions
  const decisions = useMemo(() => {
    const phases = state?.phases ?? [];
    const pendingContributions = state?.pendingContributions ?? [];
    return pendingContributions
      .filter(c => c.state === 'accepted')
      .map(c => ({
        text: c.preview || c.response,
        role: c.name || c.role,
        timestamp: c.createdAt || '',
        phase: phases[currentPhaseIndex]?.name || 'Unknown',
      }))
      .reverse(); // Most recent first
  }, [state?.pendingContributions, state?.phases, currentPhaseIndex]);

  if (!state) return null;

  if (decisions.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No decisions logged yet
      </div>
    );
  }

  const formatTime = (ts: string) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div className="flex flex-col h-full px-3 py-2 gap-2 overflow-y-auto">
      {decisions.map((decision, i) => {
        let dotClass: string;
        if (i === 0) dotClass = 'bg-primary';
        else dotClass = isDark ? 'bg-white/20' : 'bg-slate-300';

        return (
        <div
          key={`${decision.role}-${decision.timestamp}-${decision.text.slice(0, 15)}`}
          className={`flex gap-3 p-2.5 rounded-lg border ${
            isDark ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100'
          }`}
        >
          {/* Timeline dot */}
          <div className="flex flex-col items-center shrink-0 pt-1">
            <div className={`w-2 h-2 rounded-full ${dotClass}`} />
            {i < decisions.length - 1 && (
              <div className={`w-px flex-1 mt-1 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {decision.timestamp && (
                <span className={`text-xs font-mono ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                  {formatTime(decision.timestamp)}
                </span>
              )}
              <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${
                isDark ? 'bg-white/5 text-white/40' : 'bg-slate-100 text-slate-500'
              }`}>
                {decision.phase}
              </span>
            </div>
            <p className={`text-xs mt-1 leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              {decision.text.length > 150 ? decision.text.slice(0, 150) + '...' : decision.text}
            </p>
            <span className={`text-xs mt-0.5 block ${isDark ? 'text-white/25' : 'text-slate-400'}`}>
              — {decision.role}
            </span>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default DecisionLogWidget;
